import { CaretDownIcon, MagnifyingGlassIcon } from '@phosphor-icons/react'
import clsx from 'clsx'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { Flipped, Flipper } from 'react-flip-toolkit'
import { NavigationLink } from '@/src/@types/statamic'
import Button from '@/src/components/core/Button'
import { useSearchDialog } from '@/src/components/flch/components/SearchDialog'
import Container from '@/src/components/layout/Container'

export default function DesktopNav({
  scrolled = false,
  mainNavigation,
}: {
  scrolled?: boolean
  mainNavigation: NavigationLink[]
}) {
  const openSearch = useSearchDialog()

  return (
    <Container>
      <div
        className={clsx([
          'justifiy-between flex items-center transition-all',
          {
            'h-21': !scrolled,
            'h-17.5': scrolled,
          },
        ])}
      >
        <div className="flex grow items-center">
          <Link href="/" className="block">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="171"
              height="21"
              fill="none"
              className="drop-shadow-xs"
            >
              <path
                fill="#d97706"
                d="M66 7a4 4 0 0 1 4-4h8a4 4 0 0 1 2 .536V0h6v3h6a4 4 0 0 1 3 1.355A4 4 0 0 1 98 3h8a4 4 0 0 1 3 1.355A4 4 0 0 1 112 3h8a4 4 0 0 1 3 1.355A4 4 0 0 1 126 3h8a4 4 0 0 1 4 4v6h-7v8h-20a3 3 0 0 1-3-3v-5h-5v8H61a4 4 0 0 1-4-4v-4h-5v8H18a3 3 0 0 1-3-3v-5h-4v8H1v-8H0V7h1a4 4 0 0 1 4-4h8.5c1.066 0 2.033.417 2.75 1.097A3.99 3.99 0 0 1 19 3h8a4 4 0 0 1 2 .536V0h6v3h6a4 4 0 0 1 3 1.355A4 4 0 0 1 47 3h8a4 4 0 0 1 2 .536V0h9zm95-4h6a4 4 0 0 1 4 4v14h-26a4 4 0 0 1-3-1.356V21h-8v-6h7V7a4 4 0 0 1 4-4h9c.351 0 .687.063 1 .174V0h6z"
              />
              <path
                fill="#fff"
                d="M13.5 5a2 2 0 0 1 2 2H9v2h4v2H9v8H3v-8H2V9h1V7a2 2 0 0 1 2-2z"
              />
              <path
                fill="#fff"
                fillRule="evenodd"
                d="M27 5a2 2 0 0 1 2 2v12H18a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1h9V7H17a2 2 0 0 1 2-2zm-5 12h2v-5h-2z"
                clipRule="evenodd"
              />
              <path
                fill="#fff"
                d="M33 5h8a2 2 0 0 1 2 2v12h-5V7h-2v12h-5V2h2zM55 5a2 2 0 0 1 2 2v4h-5V7h-2v12h-5V7a2 2 0 0 1 2-2zM64 17h2v2h-5a2 2 0 0 1-2-2V2h5z"
              />
              <path
                fill="#fff"
                fillRule="evenodd"
                d="M78 5a2 2 0 0 1 2 2v4h-7v6h2v-4h5v5a1 1 0 0 1-1 1H69a1 1 0 0 1-1-1V7a2 2 0 0 1 2-2zm-5 4h2V7h-2z"
                clipRule="evenodd"
              />
              <path
                fill="#fff"
                d="M84 5h8a2 2 0 0 1 2 2v12h-5V7h-2v12h-5V2h2zM106 5a2 2 0 0 1 2 2v4h-5V7h-2v12h-5V7a2 2 0 0 1 2-2z"
              />
              <path
                fill="#fff"
                fillRule="evenodd"
                d="M120 5a2 2 0 0 1 2 2v4h-7v6h2v-4h5v5a1 1 0 0 1-1 1h-10a1 1 0 0 1-1-1V7a2 2 0 0 1 2-2zm-5 4h2V7h-2z"
                clipRule="evenodd"
              />
              <path
                fill="#fff"
                d="M134 5a2 2 0 0 1 2 2v4h-5V7h-2v12h-5V7a2 2 0 0 1 2-2zM140 17v2h-4v-2zM154 5a1 1 0 0 1 1 1v5h-5V7h-2v10h2v-4h5v5a1 1 0 0 1-1 1h-9a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zM159 5h8a2 2 0 0 1 2 2v12h-5V7h-2v12h-5V2h2z"
              />
            </svg>
            <span className="sr-only">Startseite</span>
          </Link>
          <MainNavigation navigation={mainNavigation} />
        </div>
        <Button
          version="gray-ghost"
          size="small"
          icon={<MagnifyingGlassIcon weight="bold" />}
          onClick={openSearch}
        >
          Suchen
        </Button>
      </div>
    </Container>
  )
}

function MainNavigation({ navigation }: { navigation: NavigationLink[] }) {
  const router = useRouter()
  const openSearch = useSearchDialog()

  const [currentIndex, setCurrentIndex] = useState<number | undefined>(
    undefined
  )
  const [navSwitched, setNavSwitched] = useState(false)
  const [animatingOut, setAnimatingOut] = useState(false)
  const leaveTimeout = useRef<NodeJS.Timeout | undefined>(undefined)

  const onMouseLeaveNavigation = () => {
    clearLeaveTimeout()
    setAnimatingOut(true)
    setNavSwitched(false)
    leaveTimeout.current = setTimeout(() => {
      setCurrentIndex(undefined)
      setAnimatingOut(false)
    }, 100)
  }

  const onMouseEnterNavItem = (index: number) => {
    clearLeaveTimeout()
    setNavSwitched(currentIndex !== undefined)
    setCurrentIndex(index)
  }

  const clearLeaveTimeout = () => {
    if (leaveTimeout.current) {
      clearTimeout(leaveTimeout.current)
      setAnimatingOut(false)
    }
  }

  useEffect(() => {
    router.events.on('routeChangeComplete', onMouseLeaveNavigation)

    return () =>
      router.events.off('routeChangeComplete', onMouseLeaveNavigation)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.events])

  return (
    <Flipper flipKey={currentIndex}>
      <ul className="ml-8 flex text-sm" onMouseLeave={onMouseLeaveNavigation}>
        {navigation.map(({ page, children }, navIndex) => (
          <li
            key={navIndex}
            className="relative"
            onMouseEnter={() =>
              children.length > 0
                ? onMouseEnterNavItem(navIndex)
                : onMouseLeaveNavigation()
            }
          >
            {page.url && (
              <Link
                href={page.url}
                className={clsx(
                  'flex items-center px-4 py-5 font-bold group-hover:text-blue-600',
                  {
                    'text-blue-600': router.asPath.startsWith(page.url),
                  }
                )}
              >
                {page.title}
                {children.length > 0 && (
                  <CaretDownIcon
                    weight="bold"
                    size={16}
                    className={clsx([
                      'translate-transform ml-1',
                      {
                        'rotate-180': currentIndex === navIndex,
                      },
                    ])}
                  />
                )}
              </Link>
            )}
            {!page.url && (
              <div
                className={clsx(
                  'flex cursor-pointer items-center px-4 py-5 font-bold group-hover:text-blue-600',
                  {
                    // TODO: validate if it's needed and goes down all children levels
                    'text-blue-600': children.some((c) => {
                      const getFirstSegment = (path: string = '') => {
                        const segments = path
                          ?.replace(/^\/+|\/+$/g, '')
                          .split('/')
                        return segments?.[0] || ''
                      }
                      return (
                        (c.page.url &&
                          getFirstSegment(router.asPath) ===
                            getFirstSegment(c.page.url)) ||
                        c.children.some(
                          (cc) =>
                            cc.page.url &&
                            getFirstSegment(router.asPath) ===
                              getFirstSegment(cc.page.url)
                        )
                      )
                    }),
                  }
                )}
              >
                {page.title}
                {children.length > 0 && (
                  <CaretDownIcon
                    weight="bold"
                    size={16}
                    className={clsx([
                      'ml-1 transition-transform',
                      {
                        'rotate-180': currentIndex === navIndex,
                      },
                    ])}
                  />
                )}
              </div>
            )}
            {currentIndex === navIndex && children.length > 0 && (
              <>
                <div className="absolute top-full min-w-max -translate-x-20 perspective-distant">
                  <Flipped flipId="dropdown">
                    <div
                      className={clsx([
                        'relative origin-top-left overflow-hidden rounded-xl bg-white shadow-navitem',
                        {
                          'animate-dropdown-out': animatingOut,
                          'animate-dropdown-in': !navSwitched && !animatingOut,
                        },
                      ])}
                      style={{ animationFillMode: 'forwards' }}
                    >
                      <Flipped inverseFlipId="dropdown" scale>
                        <div className="flex">
                          {children.map((child, childIndex) => (
                            <div
                              className={clsx('group shrink-0 px-10 py-8', {
                                'is-blue bg-linear-to-b from-blue-600 to-blue-700 text-white':
                                  childIndex === 1,
                              })}
                            >
                              {child.page.url && (
                                <Link
                                  href={child.page.url}
                                  className="-m-2 mb-6 p-2 font-bold"
                                >
                                  {child.page.title}
                                </Link>
                              )}
                              <div
                                className={clsx({
                                  'flex gap-8': child.children.every(
                                    (c) => !c.page.url
                                  ),
                                })}
                              >
                                {child.children.map((subChild) => (
                                  <>
                                    {subChild.page.url &&
                                      subChild.children.length === 0 && (
                                        <Link
                                          href={subChild.page.url}
                                          className="-m-2 mb-1 block w-50 p-2"
                                        >
                                          {subChild.page.title}
                                        </Link>
                                      )}
                                    {subChild.children.length > 0 && (
                                      <div>
                                        <p className="text-xs font-bold text-gray-600 group-[.is-blue]:text-white/80">
                                          {subChild.page.title}
                                        </p>
                                        <ul
                                          className={clsx('mt-4 grid gap-y-3', {
                                            'grid-cols-2 gap-x-8':
                                              subChild.children.length >= 8,
                                          })}
                                        >
                                          {subChild.children.map(
                                            (subSubChild, subSubChildIndex) => (
                                              <li key={subSubChildIndex}>
                                                {subSubChild.page.url && (
                                                  <Link
                                                    href={subSubChild.page.url}
                                                    className="-m-2 block min-w-50 p-2"
                                                  >
                                                    {subSubChild.page.title}
                                                  </Link>
                                                )}
                                              </li>
                                            )
                                          )}
                                        </ul>
                                      </div>
                                    )}
                                  </>
                                ))}
                              </div>
                              {navIndex === 0 && childIndex === 0 && (
                                <button
                                  onClick={() => {
                                    onMouseLeaveNavigation()
                                    openSearch()
                                  }}
                                  className="mt-8 flex items-center gap-2 font-bold text-blue-700"
                                >
                                  <MagnifyingGlassIcon
                                    weight="bold"
                                    size={20}
                                    className="shrink-0"
                                  />
                                  <span>Standort suchen</span>
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </Flipped>
                    </div>
                  </Flipped>
                </div>
                <div className="absolute top-[calc(100%-20px)] left-1/2 -translate-x-1/2">
                  <Flipped flipId="dropdown-caret">
                    <div className="z-10 w-16 translate-x-3 overflow-hidden">
                      <div className="size-6 origin-bottom-left rotate-45 bg-white shadow-lg" />
                    </div>
                  </Flipped>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
    </Flipper>
  )
}
